"use client";

import React, { useEffect, useState, useRef } from "react";
import { NavItem } from "@/interfaces/menuItems";
import MegaMenu from "../../shared/megaMenu";
import clsx from "clsx";
import NavItemEditIcon from "../navItemEditIcon/NavItemEditIcon";
import { useAppDispatch, useAppSelector } from "@/lib/store/hooks";
import { getNavItemsAsync } from "@/lib/store/features/navigation/navigationSlice";
import { getRedirectBaseUrl, hasTag } from "@/utils/helpers";
import DarkModeToggle from "@/components/shared/darkMode";
import SiteLangToggle from "@/components/shared/siteLangToggle";
import Link from "next/link";
import { useSession } from "next-auth/react";
import { HUBSPOT_PAGES } from "@/constants/common";
import NavItemEditForm from "@/app/admin/(adminTools)/navigation/builder/_navItemEditForm";
import { Button } from "@/components/shadcn/button";
import { Skeleton } from "@/components/shadcn/skeleton";
import Image from "next/image";
import { usePathname } from "next/navigation";
import HeaderHubspot from "./headerHubspot";
import ProfileNav from "./_profileNav";
import { Menu, X } from "lucide-react";
import { useLocalization } from "@/providers/LocalizationProvider";

// Custom hook for responsive header behavior
const useResponsiveHeader = () => {
  const [isHeaderHidden, setIsHeaderHidden] = useState<boolean>(true);

  useEffect(() => {
    const handleResize = () => {
      if (typeof window !== "undefined") {
        setIsHeaderHidden(window.innerWidth <= 1280);
      }
    };

    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleResize);
      handleResize(); // Initial check
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", handleResize);
      }
    };
  }, []);

  return { isHeaderHidden, setIsHeaderHidden };
};

const Header: React.FC = () => {
  let pathname = usePathname() ?? "";
  const dispatch = useAppDispatch();
  const {
    mainNavs,
    subNavs,
    profileNavs,
    showPseudoPopup,
    status: navSliceStatus,
  } = useAppSelector((state) => state.navigation);
  const { isAdmin, isAllowedLinkEditors, entityId } = useAppSelector((state) => state.profile);
  const { data: session } = useSession();
  const [showEditForm, setShowEditForm] = useState<boolean>(false);
  const [editableNavItem, setEditableNavItem] = useState<NavItem>({} as NavItem);
  const [showNextphase, setShowNextphase] = useState<boolean>(true);
  const [showMegaMenu, setShowMegaMenu] = useState<boolean>(false);
  const [currentNavItem, setCurrentNavItem] = useState<NavItem | null>(null);
  const [childNavPaneStyles, setChildNavPaneStyles] = useState<{ [key: string]: string }>({});
  const { isHeaderHidden, setIsHeaderHidden } = useResponsiveHeader();
  const [isClient, setIsClient] = useState(false);
  const megaMenuRef = useRef<HTMLDivElement>(null);
  const elementRef = useRef<HTMLDivElement>(null);

  const dict = useLocalization();

  useEffect(() => {
    setIsClient(true);

    if (typeof document !== "undefined") {
      document.addEventListener("click", handleClickOutside);
    }

    dispatch(getNavItemsAsync());

    // Cleanup event listener on component unmount
    return () => {
      if (typeof document !== "undefined") {
        document.removeEventListener("click", handleClickOutside);
      }
    };
  }, []);

  const hideAllDropDowns = (): void => {
    setShowMegaMenu(false);
  };

  const handleClickOutside = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;
    if (elementRef.current && !elementRef.current.contains(target)) {
      hideAllDropDowns();
    }
  };

  /**
   * Handles mouse enter events on navigation items to display the mega menu
   *
   * This function:
   * 1. Prevents default browser behavior and stops event propagation
   * 2. Hides any currently displayed dropdowns
   * 3. Sets the current navigation item and displays the mega menu
   * 4. Positions the mega menu directly below the navigation bar
   * 5. Applies special positioning for items with the "reposition_children" tag
   *
   * @param navItem - The navigation item being hovered
   * @param event - The mouse event that triggered this handler
   */
  const handleNavItemMouseEnter = (navItem: NavItem, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    hideAllDropDowns();
    setCurrentNavItem(navItem);
    setShowMegaMenu(true);

    const navbar = document.getElementById("main-nav-container"); // Adjust the selector as needed
    const navbarHeight = navbar?.getBoundingClientRect().height;

    // Get position of the current target element
    const rect = event.currentTarget.getBoundingClientRect();

    // Initialize styles without position, top, or width
    let newChildNavPaneStyles: { [key: string]: string } = {};

    newChildNavPaneStyles = {
      position: "absolute",
      top: `${navbarHeight}px`, // Position top based on rect
      width: `100%`, // Width based on rect
      right: "auto",
    };

    // Conditionally apply left if reposition_children tag is present
    if (hasTag("reposition_children", navItem.tags)) {
      newChildNavPaneStyles = {
        left: `${rect.left + window.scrollX}px`, // Position left based on rect
      };
    }

    setChildNavPaneStyles(newChildNavPaneStyles);
  };

  const handleMouseLeave = () => {
    setShowMegaMenu(false);
    setChildNavPaneStyles({
      position: "relative",
      top: "0px",
      left: "auto",
      right: "auto",
    });
  };

  const toggleHeader = () => {
    //Toggle between -1000px and 70px
    setShowMegaMenu(false);
    setIsHeaderHidden(!isHeaderHidden);
  };

  const toggleEditForm = (update_nav_item: boolean = true) => {
    if (update_nav_item) {
      setShowEditForm(false);
      setShowEditForm(true);
    } else {
      setShowEditForm(false);
    }
  };

  if (pathname?.includes("/session/")) {
    return null;
  }

  if (pathname && HUBSPOT_PAGES.includes(pathname)) {
    return <HeaderHubspot />;
  }

  return (
    <header
      className="sticky top-0 left-0 right-0 z-[50] bg-white dark:bg-black"
      id="main-nav-container"
      role="navigation"
      aria-label="Main Navigation"
    >
      <div className="shadow max-w-[100%] flex justify-between w-full dark:bg-black px-3 xl:px-5 py-0 h-[60px] lg:h-[76px]">
        <div className="flex justify-start items-center flex-grow-0 flex-shrink-0 relative gap-2">
          <Link
            href={"/"}
            className="hidden md:block flex-grow-0 flex-shrink-0"
            aria-label="ProZ.com Home"
          >
            <Image
              src="/next/next_assets/images/svg/proz-logo.svg"
              alt="ProZ.com Logo"
              height={132}
              width={132}
            />
          </Link>
          <Link
            href={"/"}
            className="block md:hidden flex-grow-0 flex-shrink-0 w-10 h-10"
            aria-label="ProZ.com Home"
          >
            <Image
              src="/next/next_assets/images/svg/proz-icon.svg"
              alt="ProZ.com Icon"
              height={132}
              width={132}
            />
          </Link>
        </div>

        <div
          ref={elementRef}
          className={clsx(
            "bg-white dark:bg-black top-[70px] xl:top-0 left-0 fixed xl:relative flex flex-col xl:flex-row justify-start xl:justify-center items-start xl:items-center flex-grow-0 flex-shrink-0 w-full xl:w-auto h-auto px-6 xl:px-8 py-6 xl:py-0 pb-6 xl:pb-0 shadow-lg xl:shadow-none transition-all duration-200",
            isHeaderHidden ? "translate-x-[-100%] opacity-0" : "translate-x-0 opacity-100"
          )}
          aria-expanded={!isHeaderHidden}
        >
          {navSliceStatus === "loading" &&
            [1, 2, 3, 4].map((_, index) => (
              <Skeleton key={index} className="block mx-1 !h-[2.5rem] !min-w-[10rem]" />
            ))}
          {navSliceStatus !== "loading" &&
            mainNavs.map((menuItem) => {
              const showDirectLink =
                hasTag("directlink", menuItem.tags) &&
                (menuItem.access_type !== "liv_free" || entityId > 0) &&
                (!hasTag("admin", menuItem.tags) || isAdmin) &&
                (!hasTag("nextphase", menuItem.tags) || showNextphase);

              const showStaticLink =
                !hasTag("directlink", menuItem.tags) &&
                (menuItem.access_type !== "liv_free" || entityId > 0) &&
                (!hasTag("admin", menuItem.tags) || isAdmin) &&
                (!hasTag("nextphase", menuItem.tags) || showNextphase);

              // Get the translated name from the dictionary
              const translatedName = menuItem.sid
                ? (dict?._new_site_nav?.[menuItem.sid] ?? menuItem.name)
                : menuItem.name;

              return (
                <React.Fragment key={menuItem.nav_item_id}>
                  {/* These are the menu items */}
                  {showStaticLink && (
                    <div
                      onClick={(e) => handleNavItemMouseEnter(menuItem, e)}
                      onMouseEnter={(e) => handleNavItemMouseEnter(menuItem, e)}
                      className={`group ${menuItem.tailwind} flex justify-start items-center flex-grow-0 flex-shrink-0 relative overflow-hidden gap-2 px-3 py-3 rounded-xl hover:font-semibold hover:bg-secondary hover:dark:bg-dark hover:text-primary text-dark dark:text-accent-foreground cursor-pointer transition-all duration-300 ease-in-out`}
                      tabIndex={0}
                      role="button"
                      aria-haspopup="true"
                      aria-expanded={
                        showMegaMenu && currentNavItem?.nav_item_id === menuItem.nav_item_id
                      }
                      aria-label={`${menuItem.name} menu`}
                    >
                      <p className="flex-grow-0 flex-shrink-0 text-sm text-left">
                        {translatedName}
                        {hasTag("new", menuItem.tags) && (
                          <span className="bg-green-gradient rounded-md text-sm px-2 text-slate-50 ml-1">
                            new
                          </span>
                        )}
                      </p>
                      {isAllowedLinkEditors && (
                        <span className="hidden group-hover:block">
                          <NavItemEditIcon
                            onEditIconClick={() => {
                              setEditableNavItem(menuItem);
                              toggleEditForm();
                            }}
                          />
                        </span>
                      )}
                    </div>
                  )}

                  {/* These are the My ProZ and Admin buttons */}
                  {/*{showDirectLink && (*/}
                  {/*  <Link*/}
                  {/*    onMouseEnter={(e) => hideAllDropDowns()}*/}
                  {/*    href={menuItem.link && menuItem.link.replace("ng/", "next/")}*/}
                  {/*    target={menuItem.target}*/}
                  {/*    className={`group ${menuItem.tailwind} flex flex-row mx-1 justify-start items-center flex-grow-0 flex-shrink-0 relative overflow-hidden gap-2 px-3 py-2 !rounded-2xl hover:bg-secondary hover:dark:bg-dark hover:text-primary text-dark dark:text-accent-foreground cursor-pointer`}*/}
                  {/*    aria-label={menuItem.name}*/}
                  {/*  >*/}
                  {/*    <p className="flex-grow-0 flex-shrink-0 text-sm text-left">*/}
                  {/*      {menuItem.name}*/}
                  {/*      {hasTag("new", menuItem.tags) && (*/}
                  {/*        <span className="bg-green-gradient rounded-md text-sm px-2 text-slate-50 ml-1">*/}
                  {/*          new*/}
                  {/*        </span>*/}
                  {/*      )}*/}
                  {/*    </p>*/}
                  {/*    {isAllowedLinkEditors && (*/}
                  {/*      <span className="hidden group-hover:block">*/}
                  {/*        <NavItemEditIcon*/}
                  {/*          onEditIconClick={() => {*/}
                  {/*            setEditableNavItem(menuItem);*/}
                  {/*            toggleEditForm();*/}
                  {/*          }}*/}
                  {/*        />*/}
                  {/*      </span>*/}
                  {/*    )}*/}
                  {/*  </Link>*/}
                  {/*)}*/}
                </React.Fragment>
              );
            })}
        </div>

        <div className="flex justify-start items-center flex-grow-0 flex-shrink-0 gap-8">
          <div className="flex justify-start items-center flex-grow-0 flex-shrink-0 relative gap-4">
            {session?.user ? (
              <ProfileNav
                user={session.user}
                profileNavs={profileNavs}
                mainNavs={mainNavs}
                entityId={entityId}
                isAdmin={isAdmin}
                showNextphase={showNextphase}
                isAllowedLinkEditors={isAllowedLinkEditors}
                hideAllDropDowns={hideAllDropDowns}
                setEditableNavItem={setEditableNavItem}
                toggleEditForm={toggleEditForm}
              />
            ) : (
              <div className="flex flex-row items-center gap-2">
                {isClient && (
                  <>
                    <Link
                      href={
                        getRedirectBaseUrl() +
                        "/next/signin" +
                        (pathname !== "" ? "?callback_url=" + pathname : "")
                      }
                    >
                      <Button variant="ghost">
                        {dict?._new_site_nav?.sign_in_button ?? "Sign in"}
                      </Button>
                    </Link>
                    <Link href={getRedirectBaseUrl() + "/next/signup"}>
                      <Button size="sm">
                        {dict?._new_site_nav?.register_free_button ?? "Register free"}
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            )}

            <SiteLangToggle />
            <DarkModeToggle />

            {/* Mobile menu icon */}
            <button
              onClick={toggleHeader}
              aria-label={isHeaderHidden ? "Open menu" : "Close menu"}
              aria-expanded={!isHeaderHidden}
              className="xl:hidden"
            >
              {isHeaderHidden ? (
                <Menu className="flex flex-col items-center justify-center text-primary w-8 h-8" />
              ) : (
                <X className="flex flex-col items-center justify-center w-8 h-8 text-primary" />
              )}
            </button>
          </div>
        </div>
      </div>
      {showMegaMenu && (
        <div ref={megaMenuRef}>
          <MegaMenu
            isAdmin={isAdmin}
            isAllowedLinkEditors={isAllowedLinkEditors}
            subNavs={getSubNavs(currentNavItem, subNavs, dict)}
            childNavPaneStyles={childNavPaneStyles}
            onClose={handleMouseLeave}
            onEditItemSelected={(navItem) => {
              setEditableNavItem(navItem);
              setShowEditForm(true);
            }}
          />
        </div>
      )}

      {showEditForm && (
        <NavItemEditForm
          navItem={editableNavItem}
          onEditComplete={() => toggleEditForm(false)}
          floating={true}
        />
      )}
    </header>
  );
};

const getSubNavs = (
  currentNavItem: NavItem | null,
  subNavs?: NavItem[],
  dict?: any
): { sub_nav: NavItem; child_navs: NavItem[] }[] => {
  if (!currentNavItem || !Array.isArray(subNavs) || !dict) return [];

  // Helper function to filter navigation items by parent ID
  const filterByParentId = (parentId: string, navItems: NavItem[]) =>
    navItems.filter((nav) =>
      nav.parent_ids.split(",").some((id) => parseInt(id) === parseInt(parentId))
    );

  // Get sub-navigation items for the current navigation item
  const subNavItems = filterByParentId(currentNavItem.nav_item_id, subNavs);

  // Map sub-navigation items to include their children and translated names
  return subNavItems.map((subNav) => {
    const childNavItems = filterByParentId(subNav.nav_item_id, subNavs);

    const translateName = (navItem: NavItem) =>
      navItem.sid ? (dict?._new_site_nav?.[navItem.sid] ?? navItem.name) : navItem.name;

    return {
      sub_nav: {
        ...subNav,
        translated_name: translateName(subNav),
      },
      child_navs: childNavItems.map((childNav) => ({
        ...childNav,
        translated_name: translateName(childNav),
      })),
    };
  });
};

export default Header;
